import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';

export type NetworkConnectionType = 'wifi' | 'cellular' | 'none' | 'unknown';

export type DeviceDiagnostics = {
  batteryLevel?: number;
  isCharging?: boolean;
  connectionType?: NetworkConnectionType;
};

const CONNECTION_TYPES: readonly string[] = ['wifi', 'cellular', 'none', 'unknown'];

export const toConnectionType = (value: unknown): NetworkConnectionType | undefined =>
  typeof value === 'string' && CONNECTION_TYPES.includes(value)
    ? (value as NetworkConnectionType)
    : undefined;

// A hung native bridge call must not stall the update gate, so the read is raced against a timer.
const withTimeout = <T>(read: Promise<T>, timeoutMs: number): Promise<T | undefined> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), timeoutMs);
    read.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(undefined);
      },
    );
  });

export const readDeviceDiagnostics = async (timeoutMs = 1500): Promise<DeviceDiagnostics> => {
  // In parallel so two hung bridge calls cost one timeout, not two.
  let battery;
  let network;
  try {
    [battery, network] = await Promise.all([
      withTimeout(Device.getBatteryInfo(), timeoutMs),
      // Shells built before this plugin shipped have no native Network implementation: getStatus rejects there.
      withTimeout(Network.getStatus(), timeoutMs),
    ]);
  } catch {
    return {};
  }

  const diagnostics: DeviceDiagnostics = {};
  // iOS reports -1 when the battery state is unknown.
  if (
    typeof battery?.batteryLevel === 'number' &&
    Number.isFinite(battery.batteryLevel) &&
    battery.batteryLevel >= 0 &&
    battery.batteryLevel <= 1
  ) {
    diagnostics.batteryLevel = battery.batteryLevel;
  }
  if (typeof battery?.isCharging === 'boolean') {
    diagnostics.isCharging = battery.isCharging;
  }
  const connectionType = toConnectionType(network?.connectionType);
  if (connectionType) diagnostics.connectionType = connectionType;
  return diagnostics;
};
